import { FicheItem } from '@/hooks/useProfKylianContent';
import { supabase } from '@/lib/supabase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, router, useLocalSearchParams } from 'expo-router';
import { ArrowLeft, BookOpen, CloudCheck, WifiSlash, X } from 'phosphor-react-native';
import { useEffect, useState } from 'react';
import { Dimensions, Image, Modal, RefreshControl, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';

const { width, height } = Dimensions.get('window');

// ============================================
// 📦 SYSTÈME DE CACHE
// ============================================

const CACHE_PREFIX = 'fiche_cache_';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 jours en ms

interface CachedFiche {
  content: string;
  timestamp: number;
}

const getCachedFiche = async (ficheId: string): Promise<string | null> => {
  try {
    const cached = await AsyncStorage.getItem(`${CACHE_PREFIX}${ficheId}`);
    if (!cached) return null;
    
    const { content, timestamp }: CachedFiche = JSON.parse(cached);
    
    // Vérifier si le cache est encore valide
    if (Date.now() - timestamp > CACHE_DURATION) {
      await AsyncStorage.removeItem(`${CACHE_PREFIX}${ficheId}`);
      return null;
    }
    
    return content;
  } catch (error) {
    console.error('Erreur lecture cache:', error);
    return null;
  }
};

const setCachedFiche = async (ficheId: string, content: string): Promise<void> => {
  try {
    const cacheData: CachedFiche = {
      content,
      timestamp: Date.now(),
    };
    await AsyncStorage.setItem(`${CACHE_PREFIX}${ficheId}`, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Erreur écriture cache:', error);
  }
};

// Fonction utilitaire pour vider le cache (si besoin)
export const clearFichesCache = async (): Promise<void> => {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const ficheKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));
    await AsyncStorage.multiRemove(ficheKeys);
    console.log(`Cache vidé: ${ficheKeys.length} fiches supprimées`);
  } catch (error) {
    console.error('Erreur vidage cache:', error);
  }
};

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
  amber50: '#FFFBEB',
  emerald600: '#059669',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
  purple600: '#7C3AED',
  purple100: '#EDE9FE',
  rose500: '#F43F5E',
};

// ============================================
// 🎨 PARSER MARKDOWN
// ============================================

interface ParsedElement {
  type: 'h1' | 'h2' | 'h3' | 'paragraph' | 'quote' | 'table' | 'image' | 'hr' | 'list' | 'numbered-list';
  content: string;
  rows?: string[][];
  items?: string[];
}

const parseMarkdown = (markdown: string | null | undefined): ParsedElement[] => {
  if (!markdown || typeof markdown !== 'string') {
    return [];
  }

  const lines = markdown.split('\n');
  const elements: ParsedElement[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmedLine = line.trim();

    if (trimmedLine === '') {
      i++;
      continue;
    }

    if (trimmedLine === '---') {
      elements.push({ type: 'hr', content: '' });
      i++;
      continue;
    }

    if (trimmedLine.startsWith('# ')) {
      elements.push({ type: 'h1', content: trimmedLine.substring(2) });
      i++;
      continue;
    }
    if (trimmedLine.startsWith('## ')) {
      elements.push({ type: 'h2', content: trimmedLine.substring(3) });
      i++;
      continue;
    }
    if (trimmedLine.startsWith('### ')) {
      elements.push({ type: 'h3', content: trimmedLine.substring(4) });
      i++;
      continue;
    }

    if (trimmedLine.startsWith('> ')) {
      let quoteContent = trimmedLine.substring(2);
      i++;
      while (i < lines.length && lines[i].trim().startsWith('> ')) {
        quoteContent += ' ' + lines[i].trim().substring(2);
        i++;
      }
      elements.push({ type: 'quote', content: quoteContent });
      continue;
    }

    if (trimmedLine.startsWith('![')) {
      const match = trimmedLine.match(/!\[([^\]]*)\]\(([^)]+)\)/);
      if (match) {
        elements.push({ type: 'image', content: match[2] });
      }
      i++;
      continue;
    }

    if (trimmedLine.startsWith('|')) {
      const tableRows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const row = lines[i].trim();
        if (!row.match(/^\|[\s-:|]+\|$/)) {
          const cells = row.split('|').filter(c => c.trim() !== '').map(c => c.trim());
          tableRows.push(cells);
        }
        i++;
      }
      if (tableRows.length > 0) {
        elements.push({ type: 'table', content: '', rows: tableRows });
      }
      continue;
    }

    if (/^\d+\.\s/.test(trimmedLine)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push({ type: 'numbered-list', content: '', items });
      continue;
    }

    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      const items: string[] = [];
      while (i < lines.length && (lines[i].trim().startsWith('- ') || lines[i].trim().startsWith('* '))) {
        items.push(lines[i].trim().substring(2));
        i++;
      }
      elements.push({ type: 'list', content: '', items });
      continue;
    }

    let paragraphContent = trimmedLine;
    i++;
    while (
      i < lines.length && 
      lines[i].trim() !== '' && 
      !lines[i].trim().startsWith('#') &&
      !lines[i].trim().startsWith('>') &&
      !lines[i].trim().startsWith('|') &&
      !lines[i].trim().startsWith('-') &&
      !lines[i].trim().startsWith('*') &&
      !lines[i].trim().startsWith('![') &&
      !lines[i].trim().match(/^\d+\./) &&
      lines[i].trim() !== '---'
    ) {
      paragraphContent += ' ' + lines[i].trim();
      i++;
    }
    elements.push({ type: 'paragraph', content: paragraphContent });
  }

  return elements;
};

// ============================================
// 🔤 FORMATER LE TEXTE
// ============================================

interface TextPart {
  text: string;
  bold?: boolean;
}

const formatText = (text: string): TextPart[] => {
  const parts: TextPart[] = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.substring(lastIndex, match.index) });
    }
    parts.push({ text: match[1], bold: true });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ text: text.substring(lastIndex) });
  }

  if (parts.length === 0) {
    parts.push({ text });
  }

  return parts;
};

// ============================================
// 📄 COMPOSANT PRINCIPAL
// ============================================

export default function FicheViewerScreen() {
  const { id } = useLocalSearchParams();
  const [fiche, setFiche] = useState<FicheItem | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [isFromCache, setIsFromCache] = useState(false);
  
  // ✅ État pour le zoom d'image
  const [zoomImage, setZoomImage] = useState<string | null>(null);

  useEffect(() => {
    loadFicheFromSupabase();
  }, [id]);

  // ✅ Charger la fiche depuis Supabase
  const loadFicheFromSupabase = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Récupérer les métadonnées de la fiche depuis Supabase
      const { data, error: fetchError } = await supabase
        .from('prof_kylian_fiches')
        .select('*')
        .eq('id', id as string)
        .single();

      if (fetchError) {
        console.error('Erreur fetch fiche:', fetchError);
        setError('Fiche introuvable');
        setLoading(false);
        return;
      }

      if (!data) {
        setError('Fiche introuvable');
        setLoading(false);
        return;
      }

      // Adapter les noms de colonnes (snake_case -> camelCase pour compatibilité)
      const ficheData: FicheItem = {
        id: data.id,
        title: data.title,
        matiere: data.matiere,
        theme_id: data.theme_id,
        sous_matiere: data.sous_matiere,
        chapitre_id: data.chapitre_id,
        markdown_url: data.markdown_url,
        ordre: data.ordre,
      };

      setFiche(ficheData);

      // 2. Charger le contenu markdown
      await loadMarkdownContent(ficheData);

    } catch (err) {
      console.error('Erreur chargement fiche:', err);
      setError('Impossible de charger la fiche.');
      setLoading(false);
    }
  };

  // ✅ Charger le markdown (cache d'abord, puis fetch)
  const loadMarkdownContent = async (ficheData: FicheItem) => {
    try {
      // 1. Essayer de charger depuis le cache
      const cached = await getCachedFiche(ficheData.id);
      
      if (cached) {
        console.log('📦 Fiche chargée depuis le cache:', ficheData.id);
        setMarkdownContent(cached);
        setIsFromCache(true);
        setLoading(false);
        return;
      }
      
      // 2. Sinon, fetch depuis Supabase Storage
      console.log('🌐 Téléchargement de la fiche:', ficheData.id);
      await fetchMarkdown(ficheData);
      
    } catch (err) {
      console.error('Erreur chargement markdown:', err);
      setError('Impossible de charger le contenu.');
      setLoading(false);
    }
  };

  // Fetch depuis Supabase et mise en cache
  const fetchMarkdown = async (ficheData: FicheItem) => {
    try {
      const response = await fetch(ficheData.markdown_url);
      
      if (!response.ok) {
        throw new Error(`Erreur ${response.status}`);
      }
      
      const text = await response.text();
      
      // Sauvegarder dans le cache
      await setCachedFiche(ficheData.id, text);
      
      setMarkdownContent(text);
      setIsFromCache(false);
      
    } catch (err) {
      console.error('Erreur fetch markdown:', err);
      setError('Impossible de charger la fiche. Vérifie ta connexion.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Pull to refresh - force le rechargement depuis le réseau
  const onRefresh = async () => {
    if (!fiche) return;
    setRefreshing(true);
    setIsFromCache(false);
    await fetchMarkdown(fiche);
  };

  // Parser le markdown
  const elements = parseMarkdown(markdownContent);

  // Render un élément markdown
  const renderElement = (element: ParsedElement, index: number) => {
    switch (element.type) {
      case 'h1':
        return (
          <View key={index} style={styles.h1Container}>
            <Text style={styles.h1}>{element.content}</Text>
          </View>
        );
      
      case 'h2':
        const isRetenir = element.content.includes('À retenir');
        return (
          <View key={index} style={[styles.h2Container, isRetenir && styles.h2RetenirContainer]}>
            {isRetenir && (
              <BookOpen size={22} color={colors.emerald600} weight="duotone" />
            )}
            <Text style={[styles.h2, isRetenir && styles.h2Retenir]}>{element.content}</Text>
          </View>
        );
      
      case 'h3':
        return <Text key={index} style={styles.h3}>{element.content}</Text>;
      
      case 'paragraph':
        return (
          <Text key={index} style={styles.paragraph}>
            {formatText(element.content).map((part, i) => (
              <Text key={i} style={part.bold ? styles.bold : undefined}>{part.text}</Text>
            ))}
          </Text>
        );
      
      case 'quote':
        return (
          <View key={index} style={styles.quoteContainer}>
            <View style={styles.quoteLine} />
            <Text style={styles.quoteText}>
              {formatText(element.content).map((part, i) => (
                <Text key={i} style={part.bold ? styles.quoteBold : undefined}>{part.text}</Text>
              ))}
            </Text>
          </View>
        );
      
      // ✅ Image cliquable pour zoom
      case 'image':
        return (
          <TouchableOpacity 
            key={index} 
            style={styles.imageContainer}
            onPress={() => setZoomImage(element.content)}
            activeOpacity={0.8}
          >
            <Image 
              source={{ uri: element.content }} 
              style={styles.image}
              resizeMode="contain"
            />
            <Text style={styles.imageTapHint}>Appuie pour agrandir</Text>
          </TouchableOpacity>
        );
      
      case 'table':
        if (!element.rows || element.rows.length === 0) return null;
        const headers = element.rows[0];
        const dataRows = element.rows.slice(1);
        const colWidth = 100 / headers.length;
        
        return (
          <View key={index} style={styles.tableContainer}>
            <View style={styles.tableHeader}>
              {headers.map((header, i) => (
                <View key={i} style={[styles.tableHeaderCell, { width: `${colWidth}%` }]}>
                  <Text style={styles.tableHeaderText}>{header}</Text>
                </View>
              ))}
            </View>
            {dataRows.map((row, rowIndex) => (
              <View key={rowIndex} style={[styles.tableRow, rowIndex % 2 === 1 && styles.tableRowAlt]}>
                {row.map((cell, cellIndex) => (
                  <View key={cellIndex} style={[styles.tableCell, { width: `${colWidth}%` }]}>
                    <Text style={styles.tableCellText}>
                      {formatText(cell).map((part, i) => (
                        <Text key={i} style={part.bold ? styles.bold : undefined}>{part.text}</Text>
                      ))}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
        );
      
      case 'list':
        return (
          <View key={index} style={styles.listContainer}>
            {element.items?.map((item, i) => (
              <View key={i} style={styles.listItem}>
                <View style={styles.listBullet} />
                <Text style={styles.listText}>
                  {formatText(item).map((part, j) => (
                    <Text key={j} style={part.bold ? styles.bold : undefined}>{part.text}</Text>
                  ))}
                </Text>
              </View>
            ))}
          </View>
        );
      
      case 'numbered-list':
        return (
          <View key={index} style={styles.listContainer}>
            {element.items?.map((item, i) => (
              <View key={i} style={styles.listItem}>
                <Text style={styles.listNumber}>{i + 1}.</Text>
                <Text style={styles.listText}>
                  {formatText(item).map((part, j) => (
                    <Text key={j} style={part.bold ? styles.bold : undefined}>{part.text}</Text>
                  ))}
                </Text>
              </View>
            ))}
          </View>
        );
      
      case 'hr':
        return <View key={index} style={styles.hr} />;
      
      default:
        return null;
    }
  };

  // État de chargement
  if (loading) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={[styles.container, styles.loadingContainer]}>
          <ActivityIndicator size="large" color={colors.indigo600} />
          <Text style={styles.loadingText}>Chargement de la fiche...</Text>
        </View>
      </>
    );
  }

  // État d'erreur
  if (error || !fiche) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={[styles.container, styles.errorContainer]}>
          <WifiSlash size={64} color={colors.slate300} weight="duotone" />
          <Text style={styles.errorTitle}>{error || 'Fiche introuvable'}</Text>
          <Text style={styles.errorSubtitle}>
            Vérifie ta connexion internet et réessaie.
          </Text>
          <TouchableOpacity style={styles.retryButton} onPress={loadFicheFromSupabase}>
            <Text style={styles.retryButtonText}>Réessayer</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.errorButton} onPress={() => router.back()}>
            <Text style={styles.errorButtonText}>Retour</Text>
          </TouchableOpacity>
        </View>
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      
      <View style={styles.container}>
        {/* Header */}
        <LinearGradient
          colors={[colors.indigo700, colors.indigo600]}
          style={styles.header}
        >
          <View style={styles.headerContent}>
            <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
              <ArrowLeft size={24} color={colors.white} weight="bold" />
            </TouchableOpacity>
            <View style={styles.headerTitleContainer}>
              <Text style={styles.headerTitle} numberOfLines={2}>{fiche.title}</Text>
              <View style={styles.headerSubtitleRow}>
                <Text style={styles.headerChapter}>
                  {fiche.matiere} {fiche.sous_matiere ? `• ${fiche.sous_matiere}` : ''}
                </Text>
                {isFromCache && (
                  <View style={styles.cacheBadge}>
                    <CloudCheck size={12} color={colors.emerald500} weight="fill" />
                    <Text style={styles.cacheBadgeText}>Hors-ligne</Text>
                  </View>
                )}
              </View>
            </View>
          </View>
        </LinearGradient>

        {/* Contenu */}
        <ScrollView 
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[colors.indigo600]}
              tintColor={colors.indigo600}
            />
          }
        >
          {elements.length === 0 ? (
            <View style={styles.emptyContent}>
              <Text style={styles.emptyContentText}>Contenu en cours de chargement...</Text>
            </View>
          ) : (
            elements.map((element, index) => renderElement(element, index))
          )}
          <View style={{ height: 40 }} />
        </ScrollView>
      </View>

      {/* ✅ Modal Zoom Image */}
      <Modal
        visible={zoomImage !== null}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setZoomImage(null)}
      >
        <View style={styles.zoomModalContainer}>
          <TouchableOpacity 
            style={styles.zoomCloseButton}
            onPress={() => setZoomImage(null)}
          >
            <X size={28} color={colors.white} weight="bold" />
          </TouchableOpacity>
          
          <ScrollView
            style={styles.zoomScrollView}
            contentContainerStyle={styles.zoomScrollContent}
            maximumZoomScale={3}
            minimumZoomScale={1}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            centerContent={true}
          >
            {zoomImage && (
              <Image
                source={{ uri: zoomImage }}
                style={styles.zoomImage}
                resizeMode="contain"
              />
            )}
          </ScrollView>
          
          <Text style={styles.zoomHint}>Pince pour zoomer • Appuie sur ✕ pour fermer</Text>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },

  // Header
  header: {
    paddingTop: 60,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleContainer: {
    flex: 1,
    marginLeft: 16,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  headerSubtitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  headerChapter: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.8)',
  },
  // ✅ Badge cache
  cacheBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(16, 185, 129, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
  },
  cacheBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: colors.emerald500,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 15,
    color: colors.slate500,
  },

  // Scroll content
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
  },

  // Empty content
  emptyContent: {
    padding: 40,
    alignItems: 'center',
  },
  emptyContentText: {
    fontSize: 15,
    color: colors.slate400,
  },

  // Titres
  h1Container: {
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 3,
    borderBottomColor: colors.indigo600,
  },
  h1: {
    fontSize: 28,
    fontWeight: '800',
    color: colors.slate900,
    lineHeight: 36,
  },
  h2Container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 32,
    marginBottom: 16,
  },
  h2RetenirContainer: {
    backgroundColor: colors.emerald100,
    padding: 12,
    borderRadius: 12,
    marginTop: 24,
  },
  h2: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.indigo700,
  },
  h2Retenir: {
    color: colors.emerald600,
  },
  h3: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.slate900,
    marginTop: 20,
    marginBottom: 12,
  },

  // Paragraphe
  paragraph: {
    fontSize: 16,
    color: colors.slate700,
    lineHeight: 26,
    marginBottom: 12,
  },
  bold: {
    fontWeight: '700',
    color: colors.slate900,
  },

  // Citation
  quoteContainer: {
    flexDirection: 'row',
    backgroundColor: colors.amber50,
    borderRadius: 12,
    padding: 16,
    marginVertical: 12,
  },
  quoteLine: {
    width: 4,
    backgroundColor: colors.amber500,
    borderRadius: 2,
    marginRight: 14,
  },
  quoteText: {
    flex: 1,
    fontSize: 15,
    color: colors.slate600,
    fontStyle: 'italic',
    lineHeight: 24,
  },
  quoteBold: {
    fontWeight: '700',
    color: colors.slate900,
    fontStyle: 'normal',
  },

  // Image
  imageContainer: {
    marginVertical: 20,
    alignItems: 'center',
    backgroundColor: colors.slate50,
    borderRadius: 16,
    padding: 16,
  },
  image: {
    width: width - 72,
    height: 220,
    borderRadius: 12,
  },
  // ✅ Hint pour indiquer que l'image est cliquable
  imageTapHint: {
    marginTop: 8,
    fontSize: 12,
    color: colors.slate400,
    fontStyle: 'italic',
  },

  // ✅ Styles pour le Modal Zoom
  zoomModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoomCloseButton: {
    position: 'absolute',
    top: 60,
    right: 20,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  zoomScrollView: {
    flex: 1,
    width: '100%',
  },
  zoomScrollContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoomImage: {
    width: width,
    height: height * 0.7,
  },
  zoomHint: {
    position: 'absolute',
    bottom: 50,
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.6)',
  },

  // Table
  tableContainer: {
    marginVertical: 16,
    borderRadius: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.slate200,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.indigo600,
  },
  tableHeaderCell: {
    paddingVertical: 14,
    paddingHorizontal: 12,
  },
  tableHeaderText: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.white,
  },
  tableRow: {
    flexDirection: 'row',
    backgroundColor: colors.white,
  },
  tableRowAlt: {
    backgroundColor: colors.slate50,
  },
  tableCell: {
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderRightWidth: 1,
    borderRightColor: colors.slate200,
  },
  tableCellText: {
    fontSize: 14,
    color: colors.slate700,
    lineHeight: 22,
  },

  // Liste
  listContainer: {
    marginVertical: 12,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  listBullet: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.indigo500,
    marginTop: 8,
    marginRight: 12,
  },
  listNumber: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.indigo600,
    marginRight: 10,
    minWidth: 24,
  },
  listText: {
    flex: 1,
    fontSize: 16,
    color: colors.slate700,
    lineHeight: 26,
  },

  // Séparateur
  hr: {
    height: 2,
    backgroundColor: colors.slate200,
    marginVertical: 24,
    borderRadius: 1,
  },

  // Error state
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.slate900,
    marginTop: 20,
  },
  errorSubtitle: {
    fontSize: 15,
    color: colors.slate500,
    marginTop: 8,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 24,
    backgroundColor: colors.indigo600,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
  },
  retryButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  errorButton: {
    marginTop: 12,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  errorButtonText: {
    color: colors.slate500,
    fontSize: 16,
    fontWeight: '600',
  },
});
